import {useAuth0} from '@auth0/auth0-react';
import {useSwapTokenMutation, useGetMyAccountMutation} from '../services/accounts';
import {useEffect} from 'react';
import {useAppSelector} from '../hooks';
import {
	selectAccessToken,
	selectUserProfile,
	tokenSetState,
	resetState,
	selectIsAuthenticated,
} from '../containers/accountSlice';
import {useDispatch} from 'react-redux';
import {getAccessToken} from '../cookies';

export const useAuthEffects = () => {
	const {isAuthenticated: auth0IsAuthenticated, getAccessTokenSilently, logout: auth0Logout, user: auth0User} = useAuth0();
	const dispatch = useDispatch();
	const [swapTokenMutation, {error: swapTokenError}] = useSwapTokenMutation();
	const [getMyAccountMutation] = useGetMyAccountMutation();
	const userProfile = useAppSelector(selectUserProfile);
	const token = useAppSelector(selectAccessToken);

	const swapToken = async () => {
		const auth0Token = await getAccessTokenSilently();
		await swapTokenMutation({ auth0Token });
	};

	const loadAccount = async () => {
		getMyAccountMutation(); // in case of an error, token gets removed (handled in mutation)
	};

	/**
	 * load access token into the state from cookie
	 */
	useEffect(() => {
		const cookieToken = getAccessToken();
		if (!token && cookieToken) {
			dispatch(tokenSetState(cookieToken));
		}
	}, [token]);

	/**
	 * refresh token if email is verified according to auth0, but not locally
	 */
	useEffect(() => {
		if (userProfile && !userProfile.isEmailVerified && auth0User?.email_verified) {
			swapToken();
		}
	}, [userProfile, auth0User]);

	/**
	 * clear auth0 session in case of unsuccessful token swap
	 */
	useEffect(() => {
		if (swapTokenError) {
			auth0Logout(process.env.NODE_ENV === 'development' ? { openUrl() {
				window.location.replace('localhost:3000');
			} } : undefined);
		}
	}, [swapTokenError]);

	/**
	 * 1. fetch user data if it's missing and internal token is present
	 * 2. swap auth0 token for internal token in case it's missing and auth0 token present
	 */
	useEffect(() => {
		if (userProfile) {
			return;
		} else if (token) {
			loadAccount();
		} else if (auth0IsAuthenticated) {
			swapToken(); // in case of an error, auth0 token gets removed (by effect handler above)
		}
	}, [userProfile, token, auth0IsAuthenticated]);
};

const useAuth = () => {
	const {loginWithRedirect, isAuthenticated: auth0IsAuthenticated, getAccessTokenSilently, logout: auth0Logout} = useAuth0();
	const dispatch = useDispatch();
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const isAuthLoading = auth0IsAuthenticated && !isAuthenticated;

	const logout = () => {
		auth0Logout({ logoutParams: { returnTo: 'https://www.diyfeed.com'}});
		dispatch(resetState());
	};

	const userProfile = useAppSelector(selectUserProfile);

	return {loginWithRedirect, getAccessTokenSilently, isAuthLoading, isAuthenticated, userProfile, logout};
};

export default useAuth;
